import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, BadgeCheck, Gift, Landmark } from "lucide-react";
import { motion } from "framer-motion";

export const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-accent/50 to-background py-16 md:py-24">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="free" className="mb-6">
              <Sparkles className="h-3 w-3 mr-1" />
              India's Free Career Discovery Platform
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight mb-6"
          >
            Find Your{" "}
            <span className="text-gradient-hero">Best Free Course</span>
            <br />
            in 2 Minutes
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto"
          >
            Answer 4 simple questions and get personalized recommendations 
            for free education, skill programs, and career pathways — whether you're after Class 12, in college, a graduate, or exploring your next career move.
          </motion.p>

          {/* Trust badges — moved above CTA to pre-empt "is this genuine?" */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm font-medium text-muted-foreground"
          >
            <span className="inline-flex items-center gap-1.5">
              <Landmark className="h-4 w-4 text-primary" /> Government Sources
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Gift className="h-4 w-4 text-primary" /> 100% Free
            </span>
            <span className="inline-flex items-center gap-1.5">
              <BadgeCheck className="h-4 w-4 text-primary" /> DPIIT Recognised
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-primary" /> No Registration Required
            </span>
          </motion.div>

          {/* Single dominant CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col items-center gap-4"
          >
            <Button asChild variant="hero" size="xl" className="text-base md:text-lg px-8">
              <Link to="/career-quiz">
                Take the Free Career Quiz
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/programs" className="text-primary hover:underline font-medium">
                Browse Programs
              </Link>
              <span className="text-muted-foreground" aria-hidden="true">·</span>
              <Link to="/compare" className="text-primary hover:underline font-medium">
                Compare Courses
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
